import { dobZodField, requiredStringField } from '@vvoyages/shared-ui/ui';
import { z } from 'zod';

import { DOB_VALIDATION, getDobValidationMessages, type DobValidationMessages } from '@/constants/dobValidation';
import type { FieldValidation } from '@/helpers/signUpUtils';

export const createSignUpSocialMediaSchema = (
  formValidations?: FieldValidation[],
  invalidTLDs?: string,
  dobValidation?: DobValidationMessages,
) => {
  // Cache validation lookup for better performance
  const validationMap = new Map(formValidations?.map((v) => [v.key, v]) || []);

  const getValidation = (key: string) => validationMap.get(key);
  const getErrorMessage = (key: string, defaultMessage: string) => getValidation(key)?.message || defaultMessage;

  const createRequiredField = (key: string, fieldName: string) => {
    const validation = getValidation(key);
    return requiredStringField(fieldName, {
      pattern: validation?.value,
      patternMessage: getErrorMessage(key, `Please enter a valid ${fieldName.toLowerCase()}`),
    });
  };

  // Helper for email with TLD check
  const createEmailField = () => {
    const validation = getValidation('email');
    let field = z.string().min(1, { message: 'Email is required' });

    // Add regex validation if exists
    if (validation?.value) {
      field = field.regex(new RegExp(validation.value), {
        message: getErrorMessage('email', 'Please enter a valid email address'),
      });
    }

    // Add TLD check if needed
    if (invalidTLDs) {
      const tldPattern = new RegExp(`\\.(${invalidTLDs.split(',').join('|')})$`, 'i');
      field = field.refine((value) => !tldPattern.test(value), {
        message: getErrorMessage('email', 'Please enter a valid email address'),
      });
    }

    return field;
  };

  return z.object({
    firstname: createRequiredField('firstname', 'First name'),
    lastname: createRequiredField('lastname', 'Last name'),
    email: createEmailField(),
    dob: dobZodField({
      ...(dobValidation || getDobValidationMessages({}, 'createAccount')),
      minAge: DOB_VALIDATION.minAge.createAccount,
      maxAge: DOB_VALIDATION.maxAge,
    }),
  });
};

export type SignUpSocialMediaFormData = z.infer<ReturnType<typeof createSignUpSocialMediaSchema>>;
